import React from "react";
import { useI18n } from "@/i18n";
import NewsletterForm from "@/components/NewsletterForm";
import { ArrowUpRight, TrendingUp, TrendingDown, ShieldCheck, Database, Layers, LineChart } from "lucide-react";

// Photos hosted publicly - self-contained at runtime via HTTPS CDN; if you need fully offline,
// download to /public/images and swap paths. Using Unsplash direct source URLs.
const IMG = {
  hero: "/images/hero.jpg",
  lithium: "/images/lithium.jpg",
  oil: "/images/oilgas.jpg",
  mining: "/images/mining.jpg",
  transparency: "/images/transparency.jpg",
};

function Stat({ value, label }) {
  return (
    <div className="border-l-2 border-[#B85D3D] pl-4">
      <div className="font-serif text-3xl md:text-4xl text-white leading-none">{value}</div>
      <div className="mt-2 text-xs uppercase tracking-widest text-[#c9d3e4]">{label}</div>
    </div>
  );
}

function Eyebrow({ children }) {
  return <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B85D3D] font-semibold">{children}</div>;
}

export default function Home() {
  const { t, raw } = useI18n();
  const tableRows = raw("home.dashboard.table.rows") || {};
  const rowKeys = Object.keys(tableRows);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F2447] text-white">
        <div className="absolute inset-0 opacity-25">
          <img src={IMG.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2447] via-[#0F2447]/85 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <Eyebrow>{t("home.hero.eyebrow")}</Eyebrow>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white" data-testid="hero-title">
              {t("home.hero.title")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#dae2f0] leading-relaxed max-w-3xl">{t("home.hero.lead")}</p>
            <p className="mt-4 text-sm md:text-base text-[#a8b6cf] leading-relaxed max-w-3xl">{t("home.hero.sub")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="#newsletter" className="inline-flex items-center gap-2 bg-[#B85D3D] hover:bg-[#9c4a2f] text-white px-7 py-3.5 text-sm uppercase tracking-widest rounded" data-testid="cta-primary">
                {t("home.hero.cta_primary")} <ArrowUpRight size={16} />
              </a>
              <a href="#how" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-7 py-3.5 text-sm uppercase tracking-widest rounded" data-testid="cta-secondary">
                {t("home.hero.cta_secondary")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 grid gap-6 mt-4">
            <Stat value={t("home.hero.stats.s1_value")} label={t("home.hero.stats.s1_label")} />
            <Stat value={t("home.hero.stats.s2_value")} label={t("home.hero.stats.s2_label")} />
            <Stat value={t("home.hero.stats.s3_value")} label={t("home.hero.stats.s3_label")} />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-20 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Eyebrow>{t("home.why.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl text-[#0F2447] max-w-3xl">{t("home.why.title")}</h2>
          <div className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-8">
            {["i1", "i2", "i3", "i4"].map((k, idx) => {
              const Icon = [Database, LineChart, Layers, ShieldCheck][idx];
              return (
                <div key={k} className="flex items-start gap-4 border-t border-[#d9d3c7] pt-6">
                  <div className="flex-none w-11 h-11 rounded-md bg-[#0F2447] text-white flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-[#0F2447]">{t(`home.why.items.${k}_title`)}</h3>
                    <p className="mt-2 text-sm text-[#3d4757] leading-relaxed">{t(`home.why.items.${k}_body`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Eyebrow>{t("home.sectors.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl text-[#0F2447] max-w-3xl">{t("home.sectors.title")}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { img: IMG.lithium, key: "s1" },
              { img: IMG.oil, key: "s2" },
              { img: IMG.mining, key: "s3" },
            ].map(({ img, key }) => (
              <article key={key} className="bg-[#F5F3EF] border border-[#e6e0d3] overflow-hidden rounded-sm">
                <div className="h-56 overflow-hidden">
                  <img src={img} alt={t(`home.sectors.${key}_title`)} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <span className="inline-block text-[10px] tracking-[0.22em] uppercase bg-[#0F2447] text-white px-2.5 py-1 rounded-sm">{t(`home.sectors.${key}_tag`)}</span>
                  <h3 className="mt-4 font-serif text-xl text-[#0F2447]">{t(`home.sectors.${key}_title`)}</h3>
                  <p className="mt-2 text-sm text-[#3d4757] leading-relaxed">{t(`home.sectors.${key}_body`)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="py-20 bg-[#0F2447] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Eyebrow>{t("home.how.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl text-white max-w-3xl">{t("home.how.title")}</h2>
          <p className="mt-4 text-sm md:text-base text-[#a8b6cf] max-w-2xl">{t("home.how.intro")}</p>
          <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {["s1", "s2", "s3", "s4"].map((k, idx) => (
              <li key={k} className="bg-[#152e56] border border-[#26436e] p-6 rounded-sm">
                <div className="text-[#E9B384] font-serif text-3xl">0{idx + 1}</div>
                <h3 className="mt-3 font-serif text-lg text-white">{t(`home.how.steps.${k}_title`)}</h3>
                <p className="mt-2 text-sm text-[#c9d3e4] leading-relaxed">{t(`home.how.steps.${k}_body`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* DASHBOARD PREVIEW */}
      <section className="py-20 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Eyebrow>{t("home.dashboard.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl text-[#0F2447] max-w-3xl">{t("home.dashboard.title")}</h2>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {["k1", "k2", "k3", "k4"].map((k) => {
              const delta = t(`home.dashboard.kpi.${k}_delta`);
              const positive = delta.trim().startsWith("+");
              const DeltaIcon = positive ? TrendingUp : TrendingDown;
              return (
                <div key={k} className="bg-white border border-[#e6e0d3] p-5 rounded-sm">
                  <div className="text-xs uppercase tracking-widest text-[#5a6474]">{t(`home.dashboard.kpi.${k}_label`)}</div>
                  <div className="mt-2 font-serif text-3xl text-[#0F2447]">{t(`home.dashboard.kpi.${k}_value`)}</div>
                  <div className={`mt-1 inline-flex items-center gap-1 text-xs ${positive ? "text-[#2f7a53]" : "text-[#B85D3D]"}`}>
                    <DeltaIcon size={14} /> {delta}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-white border border-[#e6e0d3] rounded-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#e6e0d3] flex items-center justify-between">
              <h3 className="font-serif text-lg text-[#0F2447]">{t("home.dashboard.table.title")}</h3>
              <span className="text-[11px] tracking-widest uppercase text-[#5a6474]">{t("home.dashboard.note")}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F3EF] text-[#0F2447]">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium">{t("home.dashboard.table.col_project")}</th>
                    <th className="text-left px-6 py-3 font-medium">{t("home.dashboard.table.col_province")}</th>
                    <th className="text-left px-6 py-3 font-medium">{t("home.dashboard.table.col_sector")}</th>
                    <th className="text-left px-6 py-3 font-medium">{t("home.dashboard.table.col_stage")}</th>
                    <th className="text-right px-6 py-3 font-medium">{t("home.dashboard.table.col_capex")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rowKeys.map((rk) => {
                    const row = tableRows[rk];
                    return (
                      <tr key={rk} className="border-t border-[#eeeae0] hover:bg-[#faf8f3]">
                        <td className="px-6 py-3 text-[#0F2447]">{row[0]}</td>
                        <td className="px-6 py-3 text-[#3d4757]">{row[1]}</td>
                        <td className="px-6 py-3 text-[#3d4757]">{row[2]}</td>
                        <td className="px-6 py-3 text-[#3d4757]">{row[3]}</td>
                        <td className="px-6 py-3 text-right font-medium text-[#0F2447]">{row[4]}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Eyebrow>{t("home.transparency.eyebrow")}</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl text-[#0F2447]">{t("home.transparency.title")}</h2>
            <p className="mt-5 text-base text-[#3d4757] leading-relaxed">{t("home.transparency.body_1")}</p>
            <p className="mt-4 text-base text-[#3d4757] leading-relaxed">{t("home.transparency.body_2")}</p>
            <ul className="mt-6 space-y-3">
              {["b1", "b2", "b3", "b4"].map((k) => (
                <li key={k} className="flex items-start gap-3 text-sm text-[#3d4757]">
                  <span className="mt-1 w-1.5 h-1.5 bg-[#B85D3D] rounded-full flex-none"></span>
                  <span>{t(`home.transparency.bullets.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="h-full min-h-[380px]">
            <img src={IMG.transparency} alt={t("home.transparency.title")} className="w-full h-full object-cover rounded-sm" />
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="py-20 bg-[#F5F3EF]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Eyebrow>{t("home.newsletter.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl text-[#0F2447]">{t("home.newsletter.title")}</h2>
          <p className="mt-4 text-base text-[#3d4757] max-w-2xl mx-auto">{t("home.newsletter.body")}</p>
          <div className="mt-10 bg-white border border-[#e6e0d3] rounded-sm p-6 md:p-10">
            <NewsletterForm />
          </div>
        </div>
      </section>
    </>
  );
}
